/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, Input, OnInit } from '@angular/core';
import { ServiceService } from "../services/service.service";
import { ServerObjectService } from "../services/server-object.service";
import { Router } from "@angular/router";
import { AuthenticationService } from "../services/authentication.service";
import { User } from "../models/user";
import { Permission } from "../models/permission";
import { ServerSettingsService } from 'app/services/server-settings.service';
import { SessionSettings } from 'app/models/session-settings';

declare var $: any;

// How long to wait before checking to see if session expired or to show the 'will expire' dialog
const ONE_SECOND = 1000;
const ONE_MINUTE = 60 * ONE_SECOND;
const CHECK_SESSION_DELAY_IN_MILLISECONDS = 30 * ONE_SECOND;

@Component({
  selector: 'app-session-expiration-dialog',
  templateUrl: './session-expiration-dialog-component.html'
})
export class SessionExpirationDialogComponent implements OnInit {

  sessionSettings: SessionSettings = null;

  sessionExpiringModal = "sessionExpiringModal";
  sessionExpiredModal = "sessionExpiredModal";

  // Time before the timeout to show the 'will expire' dialog (default is 10 minutes if not set in settings)
  prepareTimeInMinutes = 10;
  // Session timeout (default is 60 if not set in settings)
  timeoutInMinutes = 60;

  // minutes and seconds to expiration (shown in 'will expire' dialog)
  minutesToExpiration = 0;
  secondsToExpiration = 0;

  // dateExpiring and dateExpired as milliseconds. These are pre-calculated and compared every CHECK_SESSION_DELAY_IN_MILLISECONDS
  dateExpiring: number = null;
  dateExpired: number = null;
  token: string = null;

  // ID of the Timeout Task scheduled to run in the background
  timeoutId: any = null;

  constructor(
    private serverSettingsService: ServerSettingsService,
    private router: Router,
    private authenticationService: AuthenticationService) { }

  ngOnInit(): void {
    this.getSessionExpirationParameters();
  }

  async getSessionExpirationParameters(): Promise<void> {
    console.log("Refreshing Session Settings...");
    this.sessionSettings = await this.serverSettingsService.getSessionSettings();
    console.log(`Session Settings refreshed!  Session Settings: ${JSON.stringify(this.sessionSettings)}`);

    this.timeoutInMinutes = this.sessionSettings.timeout_in_minutes;
    this.prepareTimeInMinutes = this.sessionSettings.prepare_time_in_minutes;
    console.log(`TimeoutInMinutes: ${this.timeoutInMinutes}, prepareTimeInMinutes: ${this.prepareTimeInMinutes}`);

    if (this.authenticationService.getTokenExpirationDate()) {
      console.log(`Token has an expiration date: ${this.authenticationService.getTokenExpirationDate()}`);
      this.dateExpired = Date.parse("" + this.authenticationService.getTokenExpirationDate());
      this.dateExpiring = this.dateExpired - (ONE_MINUTE * this.prepareTimeInMinutes);
    } else {
      console.log("Token does not have an expiration date");
      const dateNow = new Date();
      this.dateExpiring = dateNow.getTime() + ONE_MINUTE * (this.timeoutInMinutes - this.prepareTimeInMinutes);
      this.dateExpired = dateNow.getTime() + ONE_MINUTE * (this.timeoutInMinutes);
    }

    console.log("PrepareTime Popup: " + new Date(this.dateExpiring) + ", DateExpired: " + new Date(this.dateExpired));

    const mCurrentUserInfo = JSON.parse(localStorage.getItem('currentUser')) as UserInfo;
    this.token = mCurrentUserInfo.mToken;
    setTimeout(checkForSessionExpiration.bind(this, this, this.authenticationService), CHECK_SESSION_DELAY_IN_MILLISECONDS);
  }

  // If the User logs out due to session expiration, set the JwtToken cookie to null
  logoutOfSession(): void {
    console.log(`logoutOfSession()`);

    this.authenticationService.setCookie('jwtToken', "", 0);

    // Need to clear the timeouts
    clearTimeout(this.timeoutId);

    // Go back to the login screen
    this.router.navigate(['/login']);
  }

  // In case the user is reauthorized, reset the new cookie values
  resetSession(): void {
    console.log(`resetSession()`);
    // Clear the Timeout session created by showSessionExpiring & checkForSessionExpiration
    clearTimeout(this.timeoutId);

    // Get a new session
    this.authenticationService.reauthorizeUser().then(response => {
      // Reset the dates for the next expiring and expired dates
      const dateNow = new Date();
      this.dateExpiring = dateNow.getTime() + ONE_MINUTE * (this.timeoutInMinutes - this.prepareTimeInMinutes);
      this.dateExpired = dateNow.getTime() + ONE_MINUTE * (this.timeoutInMinutes);


        // Start the session checks again
        this.getSessionExpirationParameters();
        //const self = this;
        //setTimeout(function(): void { checkForSessionExpiration(self) }, CHECK_SESSION_DELAY_IN_MILLISECONDS);
      },
      error => {
        console.log("Error reauthorizing service!", error);
        this.authenticationService.setCookie('jwtToken', "", 0);
        // If there is a failure to reauthorize the user, go back to the login screen
        this.router.navigate(['/login'], { queryParams: { returnUrl: this.router.url } });
      }
    );
  }

}

// Check the dates to see if we should show the 'will expire' or 'expired' dialog
function checkForSessionExpiration(self: SessionExpirationDialogComponent, authenticationService: AuthenticationService): void {
  console.log(`checkForSessionExpiration() - ${self?.token}`);


  //Get the Cached User Info from local storage
  const mCurrentUserInfo = JSON.parse(localStorage.getItem('currentUser')) as UserInfo;

  if (!mCurrentUserInfo) {
    // CheckForSessionExpiration: No user logged in. Ending this checkForSessionExpiration
    console.log(`checkForSessionExpiration() - ${self?.token} - No User logged in...`);
    return;
  }

  if (self.token != mCurrentUserInfo.mToken) {
    // CheckForSessionExpiration: Tokens did not match. Ending this checkForSessionExpiration
    console.log(`checkForSessionExpiration() - ${self?.token} - Tokens don't match!...`);
    return;
  }

  const dateNow = new Date().getTime();

  const differenceExpiring = self.dateExpiring - dateNow;
  const differenceExpired = self.dateExpired - dateNow;

  // If the expiring happens before the next check, expire now, while we still can
  // We don't want someone doing things then a different less friendly error shows up
  if (dateNow < self.dateExpiring && (differenceExpired + CHECK_SESSION_DELAY_IN_MILLISECONDS > self.dateExpired)) {
    self.dateExpired = self.dateExpired - CHECK_SESSION_DELAY_IN_MILLISECONDS;
  }

  console.log("Session expires at " + new Date(self.dateExpiring) + "(" + Math.floor(differenceExpiring / ONE_SECOND) + " seconds from now)");

  if (dateNow > self.dateExpired) {
    console.log("Showing Session Expired Modal & clearing out the token");

    //Clear out the cookie here as well 
    authenticationService.setCookie('jwtToken', "", 0);

    $("#" + self.sessionExpiringModal).modal('hide');
    $("#" + self.sessionExpiredModal).modal('show');

  } else if (dateNow > self.dateExpiring) {
    console.log("Showing Session Expiring Modal");
    $("#" + self.sessionExpiringModal).modal('show');

    self.minutesToExpiration = Math.floor(differenceExpired / ONE_MINUTE);
    self.secondsToExpiration = Math.floor(Math.floor(differenceExpired / ONE_SECOND) - (self.minutesToExpiration * 60));
    self.timeoutId = setTimeout(function(): void { showSessionExpiring(self, authenticationService) }, ONE_SECOND);
  } else {
    setTimeout(function(): void { checkForSessionExpiration(self, authenticationService) }, CHECK_SESSION_DELAY_IN_MILLISECONDS);
  }
}

// Show the countdown of the 'will expire' dialog
function showSessionExpiring(self: SessionExpirationDialogComponent, authenticationService: AuthenticationService): void {
  const dateNow = new Date().getTime();
  const differenceExpired = self.dateExpired - dateNow;
  self.minutesToExpiration = Math.floor(differenceExpired / ONE_MINUTE);
  self.secondsToExpiration = Math.floor(Math.floor(differenceExpired / ONE_SECOND) - (self.minutesToExpiration * 60));

  // It's possible we show this expiration dialog after the browser or tab has been in the background, and it wakes up
  // with minutes or seconds negative. We account for this along with regular behavior here
  if (self.minutesToExpiration < 0 || (self.minutesToExpiration == 0 && self.secondsToExpiration <= 0)) {
    console.log("Showing Session Expired Modal & clearing out the token");
    //Clear out the cookie as well setup for accessing the reports
    authenticationService.setCookie('jwtToken', "", 0);
    $("#" + self.sessionExpiringModal).modal('hide');
    $("#" + self.sessionExpiredModal).modal('show');
  } else {
    self.timeoutId = setTimeout(function(): void { showSessionExpiring(self, authenticationService) }, ONE_SECOND);
  }
}

class UserInfo {
  mUser: User;
  mToken: string;
  mPagePermissions: { [propKey: string]: Permission };
}
